import Decimal from "decimal.js";

import {MultiSeletcOption} from "@/ts/interface/formulaire";

import {OptionNameValue} from "../ts/interface/formulaire";
import {
  Commande,
  CommandeAPI,
  FormFormat,
  FormItem,
  FormLivraison,
  FormMagasin,
  FormOffre,
  FormOperationsCommercialesMap,
  FormOption,
  FormSelectedMagasin,
  itemOffre,
  LivraisonCommande,
  OffreOperationCommerciale,
  PrixOperationCommerciale,
} from "../ts/interface/operationCommerciale";

function formatInDecimal(valeur: string | number | null | undefined): Decimal {
  let valeurInDecimal = new Decimal(valeur || 0);

  if (valeurInDecimal.isNaN()) {
    valeurInDecimal = new Decimal(0);
  }
  return valeurInDecimal;
}

export function getOptionFormat(
  item: itemOffre,
  quantite: number = 1,
): {
  options: MultiSeletcOption[];
  formats: OptionNameValue[];
} {
  const options: MultiSeletcOption[] =
    item?.options
      ?.map(option => {
        return {
          libelle: option.nom,
          value: option.item_option_id || "",
          defaultChecked: false,
          complementLibelle: `(+${new Decimal(getPrixPalier(option.prix, quantite)).toFixed(2)}€/u)`,
          complementData: option?.prix,
        };
      })
      .filter(option => {
        return option.value !== "";
      }) || [];
  const formats: OptionNameValue[] = item?.formats
    ?.map(format => {
      return {
        libelle: format.nom,
        value: format.item_format_id || "",
        complementData: format.prix,
        complementLibelle: `(+${new Decimal(getPrixPalier(format.prix, quantite)).toFixed(2)}€/u)`,
      };
    })
    .filter(option => {
      return option.value !== "";
    });
  return {
    options,
    formats,
  };
}

/**
 * Calcule la quantité totale commandée pour un item donné,
 * en additionnant toutes les quantités des livraisons des autres magasins
 * ayant le même format et les mêmes options que l'item courant.
 *
 * @param itemId - L'identifiant de l'item concerné
 * @param currentItem - L'item de référence (celui à comparer aux autres)
 * @param magasins - Tous les magasins avec leurs items et livraisons
 * @returns La quantité totale commandée pour les items similaires
 */
export function CalculeQuantitéForPalier(
  itemId: number,
  currentItem: FormItem,
  magasins: Record<string, FormMagasin>,
): number {
  let quantiteCommande = 0;

  // additionne toutes les valeurs de livraison des autres magasin qui ont les même paramétre de format et option
  for (const key in magasins) {
    const magItem = magasins[key]?.items[`I${itemId}`];
    // Vérifie que toutes les options du magItem sont présentes dans currentItem
    const hasOptionMagItemInCurrent = magItem?.options?.every(option => {
      const findedOption = currentItem?.options?.findIndex(
        o => o.value === option.value,
      );
      return findedOption !== undefined && findedOption >= 0;
    });
    // Vérifie que toutes les options du currentItem sont présentes dans magItem
    const hasOptionCurrentInMagItem = currentItem?.options?.every(option => {
      const findedOption = magItem?.options?.findIndex(
        o => o.value === option.value,
      );
      return findedOption !== undefined && findedOption >= 0;
    });

    // si c'est vrai dans les 2 sont vrai alors est sûr qu'ils ont les même option
    const hasSameOption =
      hasOptionMagItemInCurrent && hasOptionCurrentInMagItem;

    // On vérifie également que le format est le même.
    if (
      magItem?.format?.value === currentItem?.format?.value &&
      hasSameOption
    ) {
      for (const keyLivraison in magItem?.livraisons) {
        quantiteCommande += magItem?.livraisons[keyLivraison]?.quantite || 0;
      }
    }
  }

  return quantiteCommande;
}

// permet de retouver la valeur du prix à partir de la quantité.
export function getPrixPalier(
  prix?: PrixOperationCommerciale[],
  quantite?: number,
): string {
  if (prix && quantite) {
    const indexPrix = prix
      .sort((a, b) => b.palier - a.palier)
      .findIndex(prix => prix.palier <= quantite);

    const prixUnitaire = new Decimal(
      prix?.[indexPrix]?.prix_unitaire || 0,
    ).isNaN()
      ? "0"
      : prix?.[indexPrix]?.prix_unitaire;

    return prixUnitaire;
  }
  return "";
}

export function CalcTotalPrixUnitaire(
  format: FormFormat,
  prixLivraison?: string,
  options?: FormOption[],
  quantite?: number,
): string {
  let prixTotal = new Decimal("0");
  const formatPrixUnitaire = formatInDecimal(format?.prix_unitaire);

  const livraisonPrixUnitaire = formatInDecimal(prixLivraison);

  prixTotal = prixTotal.plus(formatPrixUnitaire);

  prixTotal = prixTotal.plus(livraisonPrixUnitaire);

  if (options && options?.length > 0) {
    options.forEach(option => {
      const optionPrixUnitaire = getPrixPalier(
        option.complementData,
        quantite || 1,
      );
      const optionPrixUnaitreDecimal = formatInDecimal(optionPrixUnitaire);
      prixTotal = prixTotal.plus(optionPrixUnaitreDecimal || 0);
    });
  }

  return prixTotal.toString();
}

export function getAllPrix(
  format: FormFormat,
  prixLivraison?: PrixOperationCommerciale[],
  options?: FormOption[],
  quantiteTotal?: number,
  quantite?: number,

  forfait?: string,
): {
  prixUnitaireLivraison: string;
  prixUnitaireTotal: string;
  TotalPrixItemLivraison: string;
} {
  const prixUnitaireLivraison = getPrixPalier(
    prixLivraison,
    quantiteTotal || 1,
  );
  let prixUnitaireTotal: Decimal | string = new Decimal(
    CalcTotalPrixUnitaire(
      format,
      prixUnitaireLivraison,
      options,
      quantiteTotal,
    ),
  );
  let TotalPrixItemLivraison: Decimal | string = prixUnitaireTotal.mul(
    quantite || 0,
  );
  if (forfait && quantiteTotal) {
    TotalPrixItemLivraison = TotalPrixItemLivraison.plus(forfait);
  }
  prixUnitaireTotal = prixUnitaireTotal
    .toDecimalPlaces(2, Decimal.ROUND_DOWN)
    .toFixed(2)
    .toString();
  TotalPrixItemLivraison = TotalPrixItemLivraison.toDecimalPlaces(
    2,
    Decimal.ROUND_DOWN,
  )
    .toFixed(2)
    .toString();
  return {prixUnitaireLivraison, prixUnitaireTotal, TotalPrixItemLivraison};
}

export function hasRepiquage(offre: OffreOperationCommerciale): boolean {
  let hasRepiquage = false;
  offre?.items.forEach(item => {
    if (item.repiquage) {
      hasRepiquage = true;
    }
  });
  return hasRepiquage;
}

// Récupère une valeur imbriquée dans un objet à partir d'un chemin en string (ex: "user.profile.name").
export function getFormData(obj: any, path: string): any {
  return path.split(".").reduce((acc, part) => acc?.[part], obj);
}

export function createCommande(
  formData: FormOperationsCommercialesMap,
  operation_id: number,
  status?: "Enregistrer" | "Annuler" | "Validée",
): Commande {
  console.log("Create formData", formData);
  let commande: Commande = {
    operation_id: operation_id,
    commentaire: formData.commentaire,
    statut: status || "",
    montant_total: "0",
    ligne_commande: [],
  };

  let montant_total = new Decimal(0);
  const {offres = {}, magasin_de_livraison, has_livraison_unique} = formData;

  Object.entries(offres)?.forEach(([offreIndex, {magasins = {}}]) => {
    Object.entries(magasins)?.forEach(([magIndex, magasin]) => {
      const formatId = (id: string): number => {
        return parseInt(id.slice(1));
      };

      if (!magasin?.items) {
        return;
      }

      Object.entries(magasin?.items)?.forEach(
        ([itemIndex, {format, livraisons, options}]) => {
          let montant_ligne = new Decimal(0);
          const allOptions = options?.map(option => {
            return {
              item_option_id: option.value,
              item_option_label: option.libelle,
            };
          });
          let quantiteTotal = 0;
          let allLivraison: LivraisonCommande[] = [];
          if (livraisons) {
            Object.entries(livraisons).forEach(
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              ([_livraisonIndex, livraison]) => {
                if (livraison?.quantite) quantiteTotal += livraison.quantite;
              },
            );
            allLivraison = Object.entries(livraisons)

              .map(([LivraisonIndex]) => {
                const {TotalPrixItemLivraison, prixUnitaireTotal} = getAllPrix(
                  format,
                  livraisons[LivraisonIndex].prixPalier,
                  options,
                  quantiteTotal,
                  parseInt(livraisons[LivraisonIndex].quantite?.toString()),
                );
                montant_ligne = montant_ligne.plus(TotalPrixItemLivraison);

                const allFichierId = livraisons[LivraisonIndex]?.fichiers
                  ?.filter(fichier => fichier?.fichierId)
                  ?.map(fichier => {
                    return {fichier_id: fichier.fichierId};
                  });

                return {
                  livraison_id: formatId(LivraisonIndex),
                  quantite: livraisons[LivraisonIndex].quantite || 0,
                  prix_unitaire: prixUnitaireTotal,
                  livraison_label: livraisons[LivraisonIndex].livraison_label,
                  fichier_livraison: allFichierId || [],
                };
              })
              .filter(livraison => {
                return livraison.quantite > 0;
              });
          }

          if (allLivraison.length > 0) {
            const forfaitItemPrix = formatInDecimal(
              magasins[magIndex].items[itemIndex].forfait,
            );

            montant_total = montant_total.plus(forfaitItemPrix);
          }
          montant_total = montant_total.plus(montant_ligne);
          let offreIsCommun = true;
          if (offres[offreIndex]?.commun !== undefined) {
            offreIsCommun = offres[offreIndex]?.commun;
          }
          if (format?.value && allLivraison.length > 0) {
            (commande.ligne_commande ??= []).push({
              offre_id: formatId(offreIndex),
              offre_label: offres[offreIndex].nom,
              offre_forfait: offres[offreIndex]?.forfait,
              commun: offreIsCommun,
              magasin_id: formatId(magIndex),
              magasin_livraison_id:
                has_livraison_unique === "true"
                  ? magasin_de_livraison?.value
                  : formatId(magIndex),
              item_id: formatId(itemIndex),
              item_label: magasins[magIndex].items[itemIndex].nom || "",
              item_format_id: format.value,
              item_format_label: format.libelle,
              item_forfait: magasins[magIndex].items[itemIndex].forfait,
              option_commande: allOptions || [],
              prestataire_distribution_id:
                magasins[magIndex].items[itemIndex].prestataire_distribution
                  ?.value || null,
              repiquage: offres[offreIndex]?.repiquage?.value || "",
              livraison_commande: allLivraison,
              montant_ligne: montant_ligne.toFixed(2),
            });
          }
        },
      );
      const offreItemPrix = formatInDecimal(offres[offreIndex]?.forfait);

      montant_total = montant_total.plus(offreItemPrix);
    });
  });
  commande = {...commande, montant_total: montant_total.toFixed(2)};

  return commande;
}

export function convertCommandeApiToCommande(
  commandeApi: CommandeAPI,
): Commande | undefined {
  const commande: Commande = {
    operation_id: commandeApi.operation_id,
    commentaire: commandeApi.commentaire,
    statut: commandeApi.statut,
    montant_total: commandeApi.montant_total,
    num_commande: commandeApi.num_commande,
    date_commande: commandeApi.date_commande,
    ligne_commande: commandeApi.ligne_commande?.map(ligne => {
      return {
        offre_id: ligne.offre_id,
        offre_label: ligne.offre_nom,
        offre_forfait: ligne.offre_forfait,
        magasin_id: ligne.magasin.magasin_id,
        magasin_livraison_id: ligne.magasin_livraison.magasin_id,
        commun: ligne.commun,
        item_id: ligne.item.item_id,
        item_label: ligne.item.nom,
        item_format_id: ligne.item_format.item_format_id,
        item_format_label: ligne.item_format.nom,
        item_forfait: ligne.item.forfait.toString(),
        option_commande: ligne.option_commande.map(option => {
          return {
            item_option_id: option.item_option_id,
            item_option_label: option.nom,
          };
        }),
        prestataire_distribution_id:
          ligne.prestataire_distribution?.prestataire_distribution_id,
        repiquage: ligne.repiquage,
        livraison_commande: ligne.livraison_commande.map(livraison => {
          const fichiersId = livraison.fichier_livraison.map(fichier => {
            return {fichier_id: fichier.fichierId};
          });
          return {
            livraison_id: livraison.livraison.livraison_id,
            quantite: livraison.quantite,
            prix_unitaire: livraison.prix_unitaire.toString(),
            livraison_label: livraison.livraison.type,
            fichier_livraison: fichiersId,
          };
        }),
        montant_ligne: ligne.montant_ligne,
      };
    }),
  };

  return commande;
}

export async function convertCommandeToFormOperationCommercial(
  commande: CommandeAPI,
): Promise<FormOperationsCommercialesMap | undefined> {
  if (commande?.ligne_commande) {
    const refMagLivraison = commande?.ligne_commande[0]?.magasin_livraison;

    const has_same_livraison = commande.ligne_commande.every(
      ligne =>
        ligne.magasin_livraison.magasin_id === refMagLivraison.magasin_id,
    );
    const magLiv_dif_magCommande = commande.ligne_commande.some(
      ligne => ligne.magasin_livraison.magasin_id !== ligne.magasin.magasin_id,
    );

    const has_livraison_unique =
      has_same_livraison && magLiv_dif_magCommande ? "true" : "false";

    const formOffre: Record<string, FormOffre> = {};

    commande.ligne_commande.forEach(ligne => {
      const offreId = ligne.offre_id;
      const isCommun = ligne.commun;
      // Gestion des magasin selectionné !
      const newSelectedMag: FormSelectedMagasin[] =
        formOffre[`O${offreId}`]?.selected_magasins || [];

      const indexMag = newSelectedMag.findIndex(mag => {
        return mag.value === ligne.magasin.magasin_id;
      });

      if (indexMag < 0) {
        newSelectedMag.push({
          libelle: ligne.magasin.raison_sociale,
          value: ligne.magasin.magasin_id,
        });
      }

      const createItem = (isDefault?: boolean): Record<string, FormItem> => {
        const newItems: Record<string, FormItem> = {};
        const newOptions = ligne.option_commande.map((option): FormOption => {
          return {
            libelle: option.nom || "",
            value: option.item_option_id,
            complementData: [],
          };
        });

        const newFormat = {
          libelle: ligne.item_format.nom,
          value: ligne.item_format.item_format_id,
          complementData: [],
          prix_unitaire: "",
        };
        const newLivraisons: Record<string, FormLivraison> = {};
        for (const livraison of ligne.livraison_commande) {
          const allFichier = livraison.fichier_livraison.map(fichier => {
            const content = ["Ceci est le contenu du fichier"];

            return {
              fichier: [
                new File(content, fichier?.nom || "", {
                  type: "text/plain", // MIME type
                  lastModified: new Date().getTime(), // date modif
                }),
              ],
              fichierId: fichier.fichierId,
            };
          });

          const FiltredAllFichier = allFichier.filter(fichier => {
            return fichier.fichier !== undefined;
          });

          newLivraisons[`L${livraison.livraison.livraison_id}`] = {
            quantite: livraison.quantite,
            livraison_label: livraison.livraison.type,
            fichiers: isDefault
              ? [
                  {
                    fichier: [],
                  },
                ]
              : FiltredAllFichier,
          };
        }
        // Ajout des option à l'item
        newItems[`I${ligne.item.item_id}`] = {
          format: newFormat,
          options: newOptions,
          livraisons: newLivraisons,
        };
        return newItems;
      };
      let newCommunItems: Record<string, FormItem> = {};
      let newMagasinItems: Record<string, FormItem> = {};

      if (isCommun) {
        // récupérer la list d'item par défaut s'il a déjà créé.
        let currentCommunItems: Record<string, FormItem> = {};

        if (formOffre[`O${offreId}`]?.default?.items) {
          currentCommunItems = formOffre[`O${offreId}`]?.default?.items;
        }
        // ajoute l'item dans la liste s'il n'existe pas déjà.
        if (
          !formOffre[`O${offreId}`]?.default?.items?.[`I${ligne.item.item_id}`]
        ) {
          newCommunItems = createItem(true);
          formOffre[`O${offreId}`] = {
            ...formOffre[`O${offreId}`],
            default: {items: {...currentCommunItems, ...newCommunItems}},
          };
        }
      }
      // Offre pas commun

      let currentMagasinItems: Record<string, FormItem> = {};
      // récupérer la list d'item du magasin s'il a déjà créé.

      if (
        formOffre[`O${offreId}`]?.magasins?.[`M${ligne.magasin.magasin_id}`]
          ?.items
      ) {
        currentMagasinItems =
          formOffre[`O${offreId}`]?.magasins?.[`M${ligne.magasin.magasin_id}`]
            ?.items;
      }
      newMagasinItems = createItem(false);

      formOffre[`O${offreId}`] = {
        ...formOffre[`O${offreId}`],
        magasins: {
          ...formOffre[`O${offreId}`]?.magasins,
          [`M${ligne.magasin.magasin_id}`]: {
            items: {...currentMagasinItems, ...newMagasinItems},
          },
        },
      };

      if (
        ligne?.prestataire_distribution !== undefined &&
        ligne?.prestataire_distribution !== null
      ) {
        const currentMagasinItems: Record<string, FormItem> =
          formOffre[`O${offreId}`]?.magasins?.[`M${ligne.magasin.magasin_id}`]
            ?.items;

        const updatedItem: Record<string, FormItem> = {};
        updatedItem[`I${ligne.item.item_id}`] = {
          ...(currentMagasinItems?.[`I${ligne.item.item_id}`] || {}),
          prestataire_distribution: {
            value: ligne.prestataire_distribution.prestataire_distribution_id,
            libelle: ligne.prestataire_distribution.nom,
          },
        };

        formOffre[`O${offreId}`] = {
          ...formOffre[`O${offreId}`],
          magasins: {
            ...formOffre[`O${offreId}`]?.magasins,
            [`M${ligne.magasin.magasin_id}`]: {
              items: {
                ...currentMagasinItems,
                ...updatedItem,
              },
            },
          },
        };
      }
      formOffre[`O${offreId}`] = {
        ...formOffre[`O${offreId}`],
        commun: ligne.commun,
        selected_magasins: newSelectedMag,

        repiquage: ligne.repiquage
          ? {libelle: ligne.repiquage, value: ligne.repiquage}
          : undefined,
      };
    });

    const formOperation: FormOperationsCommercialesMap = {
      commande_id: commande.commande_id,
      commentaire: commande.commentaire,
      offres: formOffre,
      has_livraison_unique: has_livraison_unique,
      magasin_de_livraison:
        has_livraison_unique === "true"
          ? {
              libelle: refMagLivraison.raison_sociale,
              value: refMagLivraison.magasin_id,
              complementData: undefined,
            }
          : undefined,
    };

    return formOperation;
  }
  return;
}
